import { Link, Navigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { CustomerPortalHeader } from '../../../components/CustomerPortalHeader';
import {
  ActionIcon,
  ArrowRightIcon,
  CalendarIcon,
  DirectDebitIcon,
  HeadsetIcon,
  NextPaymentIcon,
  OutstandingIcon,
  PlanStatusIcon,
  TotalPaidIcon,
} from '../../../components/DashboardIcons';
import { JourneyStepper } from '../../../components/JourneyStepper';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatCard } from '../../../components/StatCard';
import { StatusBadge } from '../../../components/StatusBadge';
import { customerDashboardApi } from '../../../features/customer-dashboard/customer-dashboard.api';
import {
  actionButtonLabel,
  getSecondaryRequiredActions,
  resolveCustomerDashboardMode,
  resolveDashboardHero,
  shouldShowApplicationJourney,
  shouldShowFinancialStats,
  shouldShowOnboardingSteps,
  shouldShowRepaymentPanels,
} from '../../../features/customer-dashboard/dashboard-mode';
import { useSessionQuery as useQuery } from '../../../hooks/useSessionQuery';
import heroHouseUrl from '../../../images/dashboard-house-hero.png';
import supportAgentUrl from '../../../images/dashboard-support-agent.png';
import { formatNgn } from '../../../lib/format-currency';
import { formatBusinessDate, formatDate, lagosToday } from '../../../lib/format-date';
import { formatStatusLabel } from '../../../lib/format-status';
import { paths } from '../../../routes/paths';
import appStyles from '../AppPages.module.css';

import styles from './CustomerDashboardPage.module.css';

function timeOfDayGreeting(): string {
  const hour = new Date().getHours();

  if (hour < 12) {
    return 'Good morning';
  }

  if (hour < 17) {
    return 'Good afternoon';
  }

  return 'Good evening';
}

function daysUntilDue(dueDate: string | null): number | null {
  if (dueDate === null) {
    return null;
  }

  if (formatBusinessDate(dueDate) === '—') return null;
  const due = new Date(`${dueDate.slice(0, 10)}T00:00:00.000Z`);
  const today = new Date(`${lagosToday()}T00:00:00.000Z`);
  return Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function dueCountdownLabel(daysUntil: number | null): string | null {
  if (daysUntil === null) {
    return null;
  }

  if (daysUntil < 0) {
    return `${Math.abs(daysUntil)} day${Math.abs(daysUntil) === 1 ? '' : 's'} overdue`;
  }

  if (daysUntil === 0) {
    return 'Due today';
  }

  return `${daysUntil} day${daysUntil === 1 ? '' : 's'} left`;
}

function instalmentHint(
  paidInstalmentCount: number | null,
  totalInstalmentCount: number | null,
): string | undefined {
  if (paidInstalmentCount === null || totalInstalmentCount === null) {
    return undefined;
  }

  return `${paidInstalmentCount} of ${totalInstalmentCount} instalments paid`;
}

function heroTitleWithAmount(title: string, amount: string | null): string {
  if (amount === null || !title.includes(amount)) {
    return title;
  }

  return title.replace(amount, formatNgn(amount));
}

export function CustomerDashboardPage(): React.JSX.Element {
  const dashboard = useQuery({
    queryKey: ['customer', 'dashboard'],
    queryFn: () => customerDashboardApi.getDashboard(),
  });

  if (dashboard.isPending) {
    return <PageSpinner label="Loading your dashboard" />;
  }

  if (dashboard.isError || dashboard.data === undefined) {
    return (
      <div className={appStyles.pageStack}>
        <Alert tone="error" title="We could not load your dashboard">
          Your account data has not been changed. Check your connection and try again.
        </Alert>
        <div>
          <Button type="button" onClick={() => void dashboard.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const data = dashboard.data;
  const requiredActions = data.requiredActions ?? [];
  const onboardingSteps = data.onboardingSteps ?? [];
  const journeyStages = data.journeyStages ?? [];
  const recentPayments = data.recentPayments ?? [];
  const mode = resolveCustomerDashboardMode(data);

  if (mode === 'ONBOARDING') {
    return <Navigate to={paths.customerOnboarding} replace />;
  }

  const hero = resolveDashboardHero(data, mode);
  const secondaryActions = getSecondaryRequiredActions(requiredActions);
  const greetingName = data.customerFirstName ?? 'there';
  const planStatusLabel =
    data.applicationStatus === null ? 'Not started' : formatStatusLabel(data.applicationStatus);
  const mandateLabel =
    data.mandateStatus === null ? 'Not set up' : formatStatusLabel(data.mandateStatus);
  const daysUntil = daysUntilDue(data.nextDueDate);
  const countdownLabel = dueCountdownLabel(daysUntil);
  const paidHint = instalmentHint(data.paidInstalmentCount, data.totalInstalmentCount);
  const outstandingHint =
    data.totalInstalmentCount !== null && data.paidInstalmentCount !== null
      ? `${data.totalInstalmentCount - data.paidInstalmentCount} instalments remaining`
      : undefined;
  const showFinancialStats = shouldShowFinancialStats(mode);
  const showRepaymentPanels = shouldShowRepaymentPanels(mode);
  const showPaymentHeroVisual =
    mode === 'ATTENTION_REQUIRED' &&
    data.nextScheduleStatus !== 'PROCESSING' &&
    hero.ctaLabel !== null;

  return (
    <div className={styles.dashboard} data-dashboard-mode={mode}>
      <CustomerPortalHeader
        greeting={`${timeOfDayGreeting()}, ${greetingName}`}
        subtitle="Here's what's happening with your solar journey today."
        customerFirstName={data.customerFirstName}
        actionCount={requiredActions.length}
      />

      <section
        className={[
          styles.stateHero,
          hero.tone === 'urgent' ? styles.stateHeroUrgent : '',
          hero.tone === 'success' ? styles.stateHeroSuccess : '',
          hero.tone === 'info' ? styles.stateHeroInfo : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-labelledby="dashboard-hero-heading"
      >
        <div className={styles.heroMain}>
          <p className={styles.heroEyebrow}>{hero.eyebrow}</p>
          <h2 className={styles.heroTitle} id="dashboard-hero-heading">
            {heroTitleWithAmount(hero.title, data.nextInstalmentAmount)}
          </h2>
          <p className={styles.heroDescription}>{hero.description}</p>
          <div className={styles.heroActions}>
            {hero.ctaLabel !== null && hero.ctaPath !== null && (
              <Link className={styles.heroCta} to={hero.ctaPath}>
                {hero.ctaLabel}
                <ArrowRightIcon />
              </Link>
            )}
            {hero.secondaryCtaLabel !== null && hero.secondaryCtaPath !== null && (
              <Link className={styles.heroSecondaryCta} to={hero.secondaryCtaPath}>
                {hero.secondaryCtaLabel}
              </Link>
            )}
          </div>
        </div>

        {showPaymentHeroVisual && (
          <div className={styles.heroRight}>
            {data.nextDueDate !== null && (
              <div className={styles.dueCard}>
                <p className={styles.dueCardHeader}>
                  <CalendarIcon />
                  Due date
                </p>
                <p className={styles.dueDate}>{formatBusinessDate(data.nextDueDate)}</p>
                {countdownLabel !== null && <p className={styles.dueCountdown}>{countdownLabel}</p>}
              </div>
            )}
            <img
              className={styles.heroImage}
              src={heroHouseUrl}
              alt=""
              decoding="async"
              loading="eager"
            />
          </div>
        )}
      </section>

      {shouldShowOnboardingSteps(mode) && onboardingSteps.length > 0 && (
        <section className={styles.panel} aria-labelledby="onboarding-steps-heading">
          <div className={styles.panelHeader}>
            <h2 className={styles.panelTitle} id="onboarding-steps-heading">
              Onboarding progress
            </h2>
          </div>
          <JourneyStepper stages={onboardingSteps} />
        </section>
      )}

      {showFinancialStats && (
        <section aria-labelledby="plan-metrics-heading">
          <h2 className={appStyles.visuallyHidden} id="plan-metrics-heading">
            Plan summary
          </h2>
          <div className={styles.statRow}>
            <StatCard
              className={styles.statPlan}
              icon={<PlanStatusIcon />}
              label="Plan status"
              value={planStatusLabel}
              linkTo={paths.customerApplications}
              linkLabel="View application"
            />
            <StatCard
              className={styles.statMandate}
              icon={<DirectDebitIcon />}
              label="Direct debit"
              value={mandateLabel}
              linkTo={paths.customerContracts}
              linkLabel="View contract"
            />
            <StatCard
              className={styles.statNext}
              icon={<NextPaymentIcon />}
              label="Next payment"
              value={
                data.nextInstalmentAmount === null ? '—' : formatNgn(data.nextInstalmentAmount)
              }
              hint={
                data.nextDueDate === null
                  ? undefined
                  : `Due ${formatBusinessDate(data.nextDueDate)}`
              }
              linkTo={paths.customerRepayments}
              linkLabel="View schedule"
            />
            <StatCard
              className={styles.statPaid}
              icon={<TotalPaidIcon />}
              label="Total paid"
              value={formatNgn(data.totalPaidAmount)}
              hint={paidHint}
              linkTo={paths.customerPayments}
              linkLabel="View payments"
            />
            <StatCard
              className={styles.statOutstanding}
              icon={<OutstandingIcon />}
              label="Outstanding balance"
              value={formatNgn(data.outstandingBalance)}
              hint={outstandingHint}
              linkTo={paths.customerRepayments}
              linkLabel="View balance"
            />
          </div>
        </section>
      )}

      <div className={styles.mainGrid}>
        {shouldShowApplicationJourney(mode) && journeyStages.length > 0 && (
          <section className={styles.panel} aria-labelledby="journey-heading">
            <div className={styles.panelHeader}>
              <h2 className={styles.panelTitle} id="journey-heading">
                My Solar Journey
              </h2>
            </div>
            <JourneyStepper stages={journeyStages} />
            {paidHint !== undefined && <p className={styles.journeyFootnote}>{paidHint}</p>}
          </section>
        )}

        {secondaryActions.length > 0 && (
          <section className={styles.panel} aria-labelledby="required-actions-heading">
            <div className={styles.panelHeader}>
              <h2 className={styles.panelTitle} id="required-actions-heading">
                Other actions
              </h2>
              <p className={styles.panelMeta}>{secondaryActions.length}</p>
            </div>

            <ul className={styles.actionList}>
              {secondaryActions.map((action) => (
                <li className={styles.actionItem} key={action.code}>
                  <span className={styles.actionIcon} aria-hidden="true">
                    <ActionIcon />
                  </span>
                  <div className={styles.actionCopy}>
                    <h3 className={styles.actionTitle}>{action.title}</h3>
                    <p className={styles.actionDescription}>{action.description}</p>
                  </div>
                  <Link
                    className={`${styles.actionCta} ${action.priority === 'high' ? styles.actionCtaPrimary : ''}`}
                    to={action.actionPath}
                  >
                    {actionButtonLabel(action.code)}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {showRepaymentPanels && (
        <div className={styles.lowerGrid}>
          <section className={styles.panel} aria-labelledby="recent-payments-heading">
            <div className={styles.panelHeader}>
              <h2 className={styles.panelTitle} id="recent-payments-heading">
                Recent payments
              </h2>
              <Link className={styles.viewAllLink} to={paths.customerPayments}>
                View all →
              </Link>
            </div>

            {recentPayments.length === 0 ? (
              <p className={styles.paymentsEmpty}>No payments recorded yet.</p>
            ) : (
              <div className={styles.paymentsTableWrapper}>
                <table className={styles.paymentsTable} aria-label="Recent payments">
                  <thead>
                    <tr>
                      <th scope="col">Date</th>
                      <th scope="col">Description</th>
                      <th scope="col">Amount</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPayments.map((payment) => (
                      <tr key={payment.id}>
                        <td>{formatDate(payment.postedAt)}</td>
                        <td>{payment.internalReference}</td>
                        <td>{formatNgn(payment.amount)}</td>
                        <td>
                          <StatusBadge status={payment.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className={styles.supportPanel} aria-labelledby="support-heading">
            <div className={styles.supportCopy}>
              <div className={styles.supportHeadingRow}>
                <span className={styles.supportIconBadge} aria-hidden="true">
                  <HeadsetIcon />
                </span>
                <h2 className={styles.supportTitle} id="support-heading">
                  Need help?
                </h2>
              </div>
              <p className={styles.supportBody}>We&apos;re here for you.</p>
            </div>
            <div className={styles.supportVisual}>
              <img
                className={styles.supportImage}
                src={supportAgentUrl}
                alt=""
                decoding="async"
                loading="lazy"
              />
            </div>
          </section>
        </div>
      )}

      {mode === 'NO_APPLICATION' && (
        <section className={styles.supportPanel} aria-labelledby="support-heading-onboarding">
          <div className={styles.supportCopy}>
            <div className={styles.supportHeadingRow}>
              <span className={styles.supportIconBadge} aria-hidden="true">
                <HeadsetIcon />
              </span>
              <h2 className={styles.supportTitle} id="support-heading-onboarding">
                Need help?
              </h2>
            </div>
            <p className={styles.supportBody}>We&apos;re here for you.</p>
          </div>
          <div className={styles.supportVisual}>
            <img
              className={styles.supportImage}
              src={supportAgentUrl}
              alt=""
              decoding="async"
              loading="lazy"
            />
          </div>
        </section>
      )}
    </div>
  );
}
